/**
 * Group Members & Invitations API
 * API calls for group membership management and invitations.
 * Extracted from groupsApi.ts for file-size compliance.
 */

import { API_ENDPOINTS } from '../../../constants'
import type { GroupMemberProfile } from './groupsApi'
import type { AxiosResponse } from 'axios'
import { extractErrorMessage, isNetworkError } from '../../../helpers/apiClient.helper'
import { logger } from '../../helpers/logger.helper'

type ApiClientFn = <T = unknown>(endpoint: string, config?: { method: string; data?: unknown }) => Promise<AxiosResponse<T>>

/**
 * Fetch member profiles for a group (id, name, username, avatarId).
 * Used to show real names for members who are not in the current user's friends list.
 */
export const fetchGroupMembers = async (
  groupId: string,
  apiClientFn: ApiClientFn
): Promise<GroupMemberProfile[]> => {
  try {
    const endpoint = `${API_ENDPOINTS.groups.list}/${groupId}/members`
    const { data } = await apiClientFn<{ success: boolean; data?: GroupMemberProfile[] }>(endpoint)
    if (!data?.success || !Array.isArray(data.data)) return []
    return data.data
  } catch (error) {
    const msg = extractErrorMessage(error, 'Failed to fetch group members')
    if (msg === 'No active session') return []
    if (isNetworkError(error)) {
      logger.warn('fetchGroupMembers failed: server unreachable')
    } else {
      logger.error('fetchGroupMembers failed', { message: msg })
    }
    return []
  }
}

/**
 * Add members to a group. Only creator can add on backend.
 * Caller should refresh groups after (e.g. invalidateQueries).
 */
export const addGroupMembers = async (
  groupId: string,
  userIds: string[],
  apiClientFn: ApiClientFn
): Promise<void> => {
  if (!userIds.length) return
  const endpoint = `${API_ENDPOINTS.groups.list}/${groupId}/members`
  const { data } = await apiClientFn<{ success?: boolean; message?: string }>(endpoint, {
    method: 'PATCH',
    data: { userIds },
  })
  if (!data?.success) throw new Error(data?.message ?? 'Failed to add members')
}

/**
 * Leave a group. Removes the current user from memberIds.
 */
export const leaveGroup = async (
  groupId: string,
  apiClientFn: ApiClientFn
): Promise<void> => {
  const endpoint = `${API_ENDPOINTS.groups.list}/${groupId}/members/me`
  const { data } = await apiClientFn<{ success?: boolean; message?: string }>(endpoint, { method: 'DELETE' })
  if (!data?.success) throw new Error(data?.message ?? 'Failed to leave group')
}

/**
 * Remove a member from a group (creator only) or leave (self).
 */
export const removeGroupMember = async (
  groupId: string,
  memberId: string,
  apiClientFn: ApiClientFn
): Promise<void> => {
  const endpoint = `${API_ENDPOINTS.groups.list}/${groupId}/members/${memberId}`
  const { data } = await apiClientFn<{ success?: boolean; message?: string }>(endpoint, { method: 'DELETE' })
  if (!data?.success) throw new Error(data?.message ?? 'Failed to remove member')
}

/**
 * Accept a group invitation. Caller should invalidate both 'groups' and 'challenges' queries.
 */
export const acceptGroupInvitation = async (
  groupId: string,
  invitationId: string,
  apiClientFn: ApiClientFn
): Promise<void> => {
  const endpoint = `${API_ENDPOINTS.groups.list}/${groupId}/invitations/${invitationId}/accept`
  const { data } = await apiClientFn<{ success?: boolean; message?: string }>(endpoint, { method: 'POST' })
  if (!data?.success) throw new Error(data?.message ?? 'Failed to accept invitation')
}

/**
 * Decline a group invitation. Caller should invalidate groups query.
 */
export const declineGroupInvitation = async (
  groupId: string,
  invitationId: string,
  apiClientFn: ApiClientFn
): Promise<void> => {
  const endpoint = `${API_ENDPOINTS.groups.list}/${groupId}/invitations/${invitationId}/decline`
  const { data } = await apiClientFn<{ success?: boolean; message?: string }>(endpoint, { method: 'POST' })
  if (!data?.success) throw new Error(data?.message ?? 'Failed to decline invitation')
}

/**
 * Cancel a sent group invitation. Caller should invalidate groups query.
 */
export const cancelGroupInvitation = async (
  groupId: string,
  invitationId: string,
  apiClientFn: ApiClientFn
): Promise<void> => {
  const endpoint = `${API_ENDPOINTS.groups.list}/${groupId}/invitations/${invitationId}`
  const { data } = await apiClientFn<{ success?: boolean; message?: string }>(endpoint, { method: 'DELETE' })
  if (!data?.success) throw new Error(data?.message ?? 'Failed to cancel invitation')
}
